/**
 * PermissionWarningModal — shown once before a player enables their camera or
 * microphone for the first time, to give explicit informed consent.
 *
 * Rendered by GamePage when WebRTCContext.pendingPermission is non-null.
 */
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

export default function PermissionWarningModal({ type, onConfirm, onCancel }) {
  const { t } = useTranslation();

  const isCamera = type === 'camera';
  const title = isCamera
    ? t('players.enableCameraTitle')
    : t('players.enableMicTitle');
  const body = isCamera
    ? t('players.enableCameraWarning')
    : t('players.enableMicWarning');

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="t-bg-surface t-border border rounded-2xl p-6 w-full max-w-sm space-y-4 shadow-2xl">
        <div className="text-center space-y-2">
          <div className="text-3xl" aria-hidden="true">
            {isCamera ? '📷' : '🎤'}
          </div>
          <h2 className="text-lg font-bold t-text">{title}</h2>
          <p className="text-sm t-text-muted">{body}</p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 t-bg-input hover:opacity-80 t-text-secondary font-medium rounded-lg px-4 py-2.5 text-sm transition-colors t-focus-ring"
          >
            {t('players.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 t-btn-primary font-semibold rounded-lg px-4 py-2.5 text-sm transition-colors t-focus-ring"
          >
            {t('players.enable')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
